import { defineStore } from "pinia";
import { searchApi } from "@/api";
import { GAME_ID } from "@/stores";
import type { ThreadItem, UserInfo, ApiResponse } from "@/api";
import { useLanguageStore } from "./language";

export const useSearchStore = defineStore("search", {
  state: () => ({
    // 当前选中的 tab
    currentTab: "post" as "post" | "user",
    // 帖子筛选类型
    filterType: "views" as "views" | "dateline",
    // 是否已经搜索过
    hasSearched: false,

    // 帖子分页
    threads: [] as ThreadItem[],
    threadPage: 1,
    threadPageSize: 10,
    threadHasMore: true,
    threadLoading: false,
    threadLoadingMore: false,
    threadPagination: null as ApiResponse<ThreadItem[]>["pagination"] | null,
    // 热门关键词
    hotKeywords: [] as any[],
    // 搜索历史
    searchHistory: [] as string[],
    // 用户分页
    users: [] as UserInfo[],
    userPage: 1,
    userPageSize: 10,
    userHasMore: true,
    userLoading: false,
    userLoadingMore: false,
    userPagination: null as ApiResponse<UserInfo[]>["pagination"] | null,

    // 错误
    error: null as string | null,
  }),
  getters: {
    // 当前列表数据
    currentList: (state) => {
      return state.currentTab === "post" ? state.threads : state.users;
    },
    // 当前是否正在加载
    currentLoading: (state) => {
      return state.currentTab === "post"
        ? state.threadLoading
        : state.userLoading;
    },
    // 当前是否正在加载更多
    currentLoadingMore: (state) => {
      return state.currentTab === "post"
        ? state.threadLoadingMore
        : state.userLoadingMore;
    },
    // 当前是否还有更多数据
    currentHasMore: (state) => {
      return state.currentTab === "post"
        ? state.threadHasMore
        : state.userHasMore;
    },
  },
  actions: {
    // 设置是否已搜索
    setHasSearched(value: boolean) {
      this.hasSearched = value;
    },

    // 设置当前 tab
    setCurrentTab(tab: "post" | "user") {
      this.currentTab = tab;
    },

    // 设置筛选类型
    setFilterType(type: "views" | "dateline") {
      this.filterType = type;
    },

    // 从 localStorage 加载搜索历史
    loadSearchHistory() {
      try {
        const history = localStorage.getItem("search_history");
        if (history) {
          this.searchHistory = JSON.parse(history);
        }
      } catch (error) {
        console.error("加载搜索历史失败:", error);
        this.searchHistory = [];
      }
    },

    // 保存搜索历史到 localStorage
    saveSearchHistory() {
      try {
        localStorage.setItem(
          "search_history",
          JSON.stringify(this.searchHistory)
        );
      } catch (error) {
        console.error("保存搜索历史失败:", error);
      }
    },

    // 清除搜索列表数据
    clearSearchList() {
      this.threads = [];
      this.users = [];
      this.threadPage = 1;
      this.userPage = 1;
    },

    // 添加搜索关键词到历史记录（去重）
    addSearchHistory(keyword: string) {
      if (!keyword || keyword.trim() === "") return;

      const trimmedKeyword = keyword.trim();
      // 移除已存在的相同关键词
      this.searchHistory = this.searchHistory.filter(
        (item) => item !== trimmedKeyword
      );
      // 将新关键词添加到开头
      this.searchHistory.unshift(trimmedKeyword);
      // 限制历史记录数量（最多保存20条）
      if (this.searchHistory.length > 20) {
        this.searchHistory = this.searchHistory.slice(0, 20);
      }
      // 保存到 localStorage
      this.saveSearchHistory();
    },

    // 清空搜索历史
    clearSearchHistory() {
      this.searchHistory = [];
      try {
        localStorage.removeItem("search_history");
      } catch (error) {
        console.error("清空搜索历史失败:", error);
      }
    },

    async getHotKeywords() {
      const res = await searchApi.getHotKeywords(GAME_ID, useLanguageStore().targetLanguage);
      if (res.code === 200) {
        const adminKeys = res.data?.admin || [];
        const historyKeys = res.data?.history || [];
        this.hotKeywords = [...adminKeys, ...historyKeys];
      }
    },

    /**
     * 搜索帖子
     * @param params 搜索参数
     * @param append 是否追加数据
     */
    async searchThreads(
      { keyword, page = 1, page_size = 10, order_by }: any,
      append: boolean = false
    ) {
      this.threadLoading = append ? false : true;
      this.threadLoadingMore = append ? true : false;
      this.error = null;

      try {
        const res = await searchApi.searchThreads({
          keyword,
          page,
          page_size,
          order_by,
          game_id: GAME_ID,
          target_language: useLanguageStore().targetLanguage,
        });
        if (res.code == 200) {
          if (!append) {
            // 新请求，直接覆盖
            this.threads = res.data || [];
          } else {
            // 追加更多数据
            this.threads = [...this.threads, ...(res.data || [])];
          }
          this.threadPagination = res.pagination;
          this.threadHasMore = !!res.pagination?.has_next;
          this.threadPage = page;
        }
        return res;
      } catch (e: any) {
        this.error = e.message || "搜索帖子失败";
        throw e;
      } finally {
        this.threadLoading = false;
        this.threadLoadingMore = false;
      }
    },

    /**
     * 搜索用户
     * @param params 搜索参数
     * @param append 是否追加数据
     */
    async searchUsers(
      { username, page = 1, page_size = 10 }: any,
      append: boolean = false
    ) {
      this.userLoading = append ? false : true;
      this.userLoadingMore = append ? true : false;
      this.error = null;

      try {
        const res = await searchApi.searchUsers({
          username,
          page,
          page_size,
          game_id: GAME_ID,
        });
        if (res.code == 200) {
          if (!append) {
            // 新请求，直接覆盖
            this.users = res.data || [];
          } else {
            // 追加更多数据
            this.users = [...this.users, ...(res.data || [])];
          }
          this.userPagination = res.pagination;
          this.userHasMore = !!res.pagination?.has_next;
          this.userPage = page;
        }
        return res;
      } catch (e: any) {
        this.error = e.message || "搜索用户失败";
        throw e;
      } finally {
        this.userLoading = false;
        this.userLoadingMore = false;
      }
    },

    /**
     * 加载更多帖子
     */
    async loadMoreThreads({ keyword, order_by }: any) {
      if (!this.threadHasMore || this.threadLoading || this.threadLoadingMore) {
        return;
      }
      const nextPage = this.threadPage + 1;
      await this.searchThreads(
        {
          keyword,
          order_by,
          page: nextPage,
          page_size: this.threadPageSize,
        },
        true
      );
    },

    /**
     * 加载更多用户
     */
    async loadMoreUsers({ username }: any) {
      if (!this.userHasMore || this.userLoading || this.userLoadingMore) {
        return;
      }
      const nextPage = this.userPage + 1;
      await this.searchUsers(
        { username, page: nextPage, page_size: this.userPageSize },
        true
      );
    },

    /**
     * 搜索当前 tab 的数据
     */
    async searchCurrentTab(keyword: string) {
      let response: any;
      if (this.currentTab === "post") {
        response = await this.searchThreads(
          {
            keyword,
            page: 1,
            page_size: this.threadPageSize,
            order_by: this.filterType,
          },
          false
        );
      } else {
        response = await this.searchUsers(
          {
            username: keyword,
            page: 1,
            page_size: this.userPageSize,
          },
          false
        );
      }
      return response;
    },

    /**
     * 批量更新帖子关注状态，根据authorid更新
     */
    updateSearchThreadsFollowStatus(authorid: number, follow_action: number) {
      this.threads.forEach((item) => {
        if (item.authorid === authorid) {
          item.is_author_followed = follow_action;
        }
      });
    },

    /**
     * 加载更多当前 tab 的数据
     */
    async loadMoreCurrentTab(keyword: string) {
      if (this.currentTab === "post") {
        await this.loadMoreThreads({
          keyword,
          order_by: this.filterType,
        });
      } else {
        await this.loadMoreUsers({ username: keyword });
      }
    },

    /**
     * 重置帖子数据
     */
    resetThreads() {
      this.threads = [];
      this.threadPage = 1;
      this.threadHasMore = true;
      this.threadPagination = null;
      this.threadLoading = false;
      this.threadLoadingMore = false;
      this.error = null;
    },

    /**
     * 重置用户数据
     */
    resetUsers() {
      this.users = [];
      this.userPage = 1;
      this.userHasMore = true;
      this.userPagination = null;
      this.userLoading = false;
      this.userLoadingMore = false;
      this.error = null;
    },

    /**
     * 重置所有数据
     */
    resetAll() {
      this.resetThreads();
      this.resetUsers();
      this.hasSearched = false;
    },
  },
});
